import Humanize from "humanize-plus";
import { useMemo } from "react";

import { useLang } from "../lib/hooks";
import { NumberConverter, NumberDelimeter } from "../lib/number-converter";

type NumberFormatProps = {
  number: number;
  compact?: boolean;
  delimeter?: NumberDelimeter;
  precision?: number;
  className?: string;
  fractionalClassname?: string;
  truncateIfFractionalZero?: boolean;
  suffix?: React.ReactNode;
};

export const NumberFormat: React.FC<NumberFormatProps> = ({
  number: numberRaw,
  compact,
  delimeter,
  precision,
  className,
  fractionalClassname,
  truncateIfFractionalZero = false,
  suffix,
}) => {
  const { lang } = useLang();

  const number = useMemo(
    () =>
      delimeter
        ? NumberConverter.format(numberRaw ?? 0, delimeter, precision, false)
        : NumberConverter.locFormat(numberRaw ?? 0, lang, precision, false),
    [delimeter, lang, numberRaw, precision],
  );

  const parts = useMemo(() => number.split("."), [number]);
  const intPart = useMemo(() => {
    if (compact === true) {
      return Humanize.compactInteger(numberRaw, precision);
    }

    return parts[0];
  }, [compact, parts, numberRaw, precision]);

  const fractPart = useMemo(() => {
    // just in case
    const fract = parts.length === 1 ? "0" : parts[1];
    if (truncateIfFractionalZero === true && parseInt(fract ?? "0", 10) === 0)
      return null;

    return fract;
  }, [parts, truncateIfFractionalZero]);

  return (
    <span className={className}>
      {intPart}
      {fractPart != null && (
        <span className={fractionalClassname}>.{fractPart}</span>
      )}
      {suffix}
    </span>
  );
};
